import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import { Box, Dialog, IconButton, Link, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { HelpCircle } from "lucide-react";
import React from "react";
import { useBranding } from "../../BrandingContext";
import { useModalStyles } from "../common/useModalStyles";

export type TroubleSigninProps = {
  open: boolean;
  onClose: () => void;
};

type ModalTokens = ReturnType<typeof useModalStyles>["tokens"];

const TipRow = ({
  text,
  alignTop = false,
  tokens,
}: {
  text: React.ReactNode;
  alignTop?: boolean;
  tokens: ModalTokens;
}) => (
  <Box display="flex" alignItems={alignTop ? "flex-start" : "center"} gap={1.5}>
    <CheckCircleIcon
      sx={{
        fontSize: 20,
        color: tokens.accent,
        mt: alignTop ? "4px" : 0,
        flexShrink: 0,
      }}
    />
    <Box
      component="div"
      sx={{ color: tokens.textPrimary, fontSize: "0.875rem", lineHeight: 1.5, flex: 1, minWidth: 0 }}
    >
      {text}
    </Box>
  </Box>
);

const TroubleSignin: React.FC<TroubleSigninProps> = ({ open, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { tokens, paperSx, headerSx, iconBoxSx, bodySx, closeIconSx } = useModalStyles();
  const branding = useBranding();
  const { supportContactInformation } = branding || {};

  const linkSx = {
    color: tokens.accent,
    cursor: "pointer" as const,
    fontWeight: 700,
    textDecoration: "none",
    "&:hover": { textDecoration: "underline" },
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: { ...paperSx(isMobile), maxWidth: { sm: 560 } } }}
      aria-labelledby="trouble-signin-dialog-title"
    >
      <Box sx={headerSx}>
        <Box sx={iconBoxSx(tokens.accent)}>
          <HelpCircle size={22} color={tokens.accent} strokeWidth={1.8} />
        </Box>
        <Box sx={{ minWidth: 0, pr: 4 }}>
          <Typography
            id="trouble-signin-dialog-title"
            fontWeight={700}
            sx={{
              fontSize: "1.1rem",
              color: tokens.textPrimary,
              lineHeight: 1.3,
            }}
          >
            Trouble signing in?
          </Typography>
          <Typography sx={{ fontSize: "0.8rem", color: tokens.textSecondary, mt: 0.15 }}>
            Common fixes and how to get help
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={closeIconSx} aria-label="Close">
          <CloseIcon sx={{ fontSize: "1rem" }} />
        </IconButton>
      </Box>

      <Box sx={bodySx}>
        <Typography sx={{ color: tokens.textPrimary, fontSize: "0.95rem", mb: 0.5 }}>
          If you&apos;re seeing issues with signing in, please try the following:
        </Typography>

        <Stack spacing={2} mt={2}>
          <TipRow tokens={tokens} text="Your username is your email address." />
          <TipRow
            tokens={tokens}
            alignTop
            text="Your password is case-sensitive. Double-check capitalization and any extra spaces."
          />
          <TipRow
            tokens={tokens}
            alignTop
            text={
              <>
                <Typography component="span" variant="body2" sx={{ color: tokens.textPrimary, display: "block" }}>
                  Hard-refresh the page to clear cached content:
                </Typography>
                <Typography variant="body2" sx={{ color: tokens.textSecondary, display: "block", mt: 0.5 }}>
                  <Box component="strong" sx={{ color: tokens.textPrimary }}>
                    Windows:
                  </Box>{" "}
                  Ctrl + F5 or Ctrl + Shift + R
                </Typography>
                <Typography variant="body2" sx={{ color: tokens.textSecondary, display: "block" }}>
                  <Box component="strong" sx={{ color: tokens.textPrimary }}>
                    Mac:
                  </Box>{" "}
                  Cmd + Shift + R
                </Typography>
              </>
            }
          />
          <TipRow
            tokens={tokens}
            text={
              <>
                Use the <strong>&quot;Forgot Password?&quot;</strong> option if you need to reset your password.
              </>
            }
          />
          <TipRow
            tokens={tokens}
            alignTop
            text={
              <>
                If you still can&apos;t sign in, please{" "}
                {supportContactInformation?.supportFormUrl ? (
                  <Box
                    component="span"
                    onClick={() => {
                      window.open(supportContactInformation?.supportFormUrl, "_blank");
                    }}
                    sx={linkSx}
                  >
                    contact support
                  </Box>
                ) : (
                  <Box component="span">
                    contact support on{" "}
                    <Link href={`tel:${supportContactInformation?.supportPhoneHref}`} sx={linkSx}>
                      {supportContactInformation?.supportPhone}
                    </Link>
                  </Box>
                )}
                {" "}
                — we can reset your password and issue you a temporary one.
              </>
            }
          />
        </Stack>
      </Box>
    </Dialog>
  );
};

export default TroubleSignin;
